import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { getConsent, hasDecision, saveConsent } from '@/lib/consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    // Show the banner if no decision has been made yet
    if (!hasDecision()) {
      setVisible(true);
    }
    const existing = getConsent();
    if (existing) setAnalytics(!!existing.analytics);

    const openHandler = () => {
      const c = getConsent();
      setAnalytics(c ? !!c.analytics : true);
      setShowCustomize(true);
      setVisible(true);
    };
    window.addEventListener('open_cookie_preferences', openHandler);
    return () => window.removeEventListener('open_cookie_preferences', openHandler);
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true });
    setVisible(false);
    setShowCustomize(false);
  };

  const rejectAll = () => {
    saveConsent({ analytics: false });
    setVisible(false);
    setShowCustomize(false);
  };

  const savePreferences = () => {
    saveConsent({ analytics });
    setVisible(false);
    setShowCustomize(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop for customize modal */}
      {showCustomize && (
        <div
          className="fixed inset-0 bg-black/50 z-[9998]"
          onClick={() => setShowCustomize(false)}
          data-testid="cookie-consent-backdrop"
        />
      )}

      {/* Main consent bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-gray-200 shadow-2xl"
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-6">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            {/* Copy */}
            <div className="flex-1">
              <h3 className="text-xl font-bold text-[#0F5132] mb-2">
                Cookie Preferences
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed max-w-4xl">
                We use cookies and similar technologies to help our website function, improve performance, analyze traffic, protect forms from spam, and understand how visitors interact with our site. Some cookies are necessary for the website to work. Optional cookies, including analytics and third-party services, will only be used if you give consent. You can accept all cookies, reject optional cookies, or customize your preferences at any time.
              </p>
              <div className="mt-3 text-sm flex items-center gap-2">
                <Link to="/privacy" className="text-[#0F5132] font-medium hover:underline" data-testid="cookie-privacy-link">
                  Privacy Policy
                </Link>
                <span className="text-gray-400">|</span>
                <Link to="/cookies" className="text-[#0F5132] font-medium hover:underline" data-testid="cookie-policy-link">
                  Cookie Policy
                </Link>
              </div>
            </div>

            {/* Actions - stacked vertically on right */}
            <div className="flex flex-col gap-2.5 w-full sm:w-auto lg:min-w-[180px]">
              <button
                onClick={acceptAll}
                className="px-8 py-2.5 bg-[#0F5132] hover:bg-[#0a3d24] text-white text-sm font-semibold rounded-md transition-colors shadow-sm"
                data-testid="cookie-accept-all-btn"
              >
                Accept All
              </button>
              <button
                onClick={rejectAll}
                className="px-8 py-2.5 bg-[#DC3545] hover:bg-[#b02a37] text-white text-sm font-semibold rounded-md transition-colors shadow-sm"
                data-testid="cookie-reject-all-btn"
              >
                Reject All
              </button>
              <button
                onClick={() => setShowCustomize(true)}
                className="px-8 py-2.5 bg-[#F5B700] hover:bg-[#d9a300] text-white text-sm font-semibold rounded-md transition-colors shadow-sm"
                data-testid="cookie-customize-btn"
              >
                Customize
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Customize modal */}
      {showCustomize && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          data-testid="cookie-customize-modal"
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-[#0F5132]">Customize Cookie Preferences</h3>
              <button
                onClick={() => setShowCustomize(false)}
                className="text-gray-400 hover:text-gray-700 transition-colors"
                aria-label="Close"
                data-testid="cookie-customize-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Necessary */}
              <div className="flex items-start justify-between gap-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900 text-sm">Strictly Necessary</h4>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-white">Always active</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Required for the site to work — routing, authentication, and security. Cannot be disabled.
                  </p>
                </div>
                <div className="mt-1 flex-shrink-0">
                  <div className="w-10 h-5 bg-[#0F5132] rounded-full relative opacity-70">
                    <div className="w-4 h-4 bg-white rounded-full absolute top-0.5 right-0.5" />
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4 p-4 border border-gray-200 rounded-md">
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">Analytics</h4>
                  <p className="text-xs text-gray-600 mt-1">
                    Helps us understand which pages are popular and how visitors move through the site (Google Analytics 4, PostHog).
                    No data is collected until you enable this.
                  </p>
                </div>
                <button
                  onClick={() => setAnalytics((v) => !v)}
                  className={`mt-1 flex-shrink-0 w-10 h-5 rounded-full relative transition-colors ${
                    analytics ? 'bg-[#0F5132]' : 'bg-gray-300'
                  }`}
                  aria-pressed={analytics}
                  data-testid="cookie-toggle-analytics"
                >
                  <div
                    className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all ${
                      analytics ? 'right-0.5' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={rejectAll}
                className="px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 text-sm font-semibold rounded-md transition-colors"
                data-testid="cookie-customize-reject-btn"
              >
                Reject All
              </button>
              <button
                onClick={savePreferences}
                className="px-5 py-2.5 bg-[#0F5132] hover:bg-[#0a3d24] text-white text-sm font-semibold rounded-md transition-colors"
                data-testid="cookie-customize-save-btn"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
